import Vue from 'vue'
import Vuex from 'vuex'
import { login, logout, getInfo } from '@/api/user'
import { normalizeStringArray, resolveDefaultHomePath } from '@/utils/permission'

Vue.use(Vuex)

function isMockableNetworkError(error) {
  const message = error && error.message ? String(error.message) : ''
  const code = error && error.code ? String(error.code) : ''
  return /Could not proxy request|ECONNREFUSED|Network Error/i.test(message) || code === 'ECONNREFUSED'
}

function decodeJwtPayload(token = '') {
  try {
    const parts = String(token).split('.')
    if (parts.length < 2) return {}
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const normalized = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    return JSON.parse(window.atob(normalized))
  } catch (error) {
    return {}
  }
}

function resolveIsAdminFlag(data = {}, token = '') {
  const user = data.user || {}
  const payload = decodeJwtPayload(token || data.token || '')
  return Boolean(
    data.is_super_admin ||
    data.is_admin ||
    user.is_super_admin ||
    user.is_admin ||
    payload.is_super_admin ||
    payload.is_admin
  )
}

function getStatusBlockReason(data = {}) {
  const user = data.user || {}
  const role = data.role || {}

  if (Object.prototype.hasOwnProperty.call(user, 'status') && Number(user.status) !== 1) {
    return '当前账号已被禁用，不允许登录后台管理系统'
  }

  if (role && Object.keys(role).length > 0 && Object.prototype.hasOwnProperty.call(role, 'status') && Number(role.status) !== 1) {
    return '当前账号绑定的角色已被禁用，不允许登录后台管理系统'
  }

  return ''
}

export default new Vuex.Store({
  modules: {
    user: {
      namespaced: true,
      state: {
        token: localStorage.getItem('vue_admin_token') || '',
        user_info: null,
        role_info: null,
        name: '',
        avatar: '',
        last_login_at: '',
        is_admin: false,
        profile_loaded: false,
        permission_codes: [],
        menu_keys: [],
        default_home: '',
        permission_version: ''
      },
      mutations: {
        SET_TOKEN: (state, token) => {
          state.token = token
          localStorage.setItem('vue_admin_token', token)
        },
        SET_USER_INFO: (state, userInfo) => {
          state.user_info = userInfo || null
        },
        SET_ROLE_INFO: (state, roleInfo) => {
          state.role_info = roleInfo || null
        },
        SET_NAME: (state, name) => {
          state.name = name
        },
        SET_AVATAR: (state, avatar) => {
          state.avatar = avatar
        },
        SET_LAST_LOGIN_AT: (state, time) => {
          state.last_login_at = time
        },
        SET_IS_ADMIN: (state, isAdmin) => {
          state.is_admin = Boolean(isAdmin)
        },
        SET_PROFILE_LOADED: (state, loaded) => {
          state.profile_loaded = Boolean(loaded)
        },
        SET_PERMISSION_CODES: (state, permissionCodes) => {
          state.permission_codes = normalizeStringArray(permissionCodes)
        },
        SET_MENU_KEYS: (state, menuKeys) => {
          state.menu_keys = normalizeStringArray(menuKeys)
        },
        SET_DEFAULT_HOME: (state, defaultHome) => {
          state.default_home = typeof defaultHome === 'string' ? defaultHome.trim() : ''
        },
        SET_PERMISSION_VERSION: (state, version) => {
          state.permission_version = version || ''
        },
        RESET_STATE: (state) => {
          state.token = ''
          state.user_info = null
          state.role_info = null
          state.name = ''
          state.avatar = ''
          state.last_login_at = ''
          state.is_admin = false
          state.profile_loaded = false
          state.permission_codes = []
          state.menu_keys = []
          state.default_home = ''
          state.permission_version = ''
          localStorage.removeItem('vue_admin_token')
        }
      },
      actions: {
        // user login
        login({ commit }, userInfo) {
          const { mobile, password } = userInfo
          return new Promise((resolve, reject) => {
            login({ mobile: mobile.trim(), password: password }).then(response => {
              const { data } = response
              const blockReason = getStatusBlockReason(data)
              if (blockReason) {
                commit('RESET_STATE')
                reject(new Error(blockReason))
                return
              }
              const isAdmin = resolveIsAdminFlag(data, data.token)
              if (!isAdmin) {
                commit('RESET_STATE')
                reject(new Error('当前账号无登录权限，请联系管理员开通'))
                return
              }
              commit('SET_TOKEN', data.token)
              commit('SET_USER_INFO', data.user || null)
              commit('SET_ROLE_INFO', data.role || null)
              commit('SET_IS_ADMIN', isAdmin)
              commit('SET_PERMISSION_CODES', [])
              commit('SET_MENU_KEYS', [])
              commit('SET_DEFAULT_HOME', '')
              commit('SET_PERMISSION_VERSION', '')
              commit('SET_PROFILE_LOADED', false)
              if (data.user) {
                commit('SET_NAME', data.user.nickname || data.user.mobile)
                commit('SET_AVATAR', data.user.avatar)
                commit('SET_LAST_LOGIN_AT', data.user.last_login_at)
              }
              resolve()
            }).catch(error => {
              // 仅在本地无后端联调时允许演示账号走 mock，真实业务错误不能进入后台
              if (isMockableNetworkError(error) && (mobile === 'admin' || mobile === '13000000000')) {
                console.warn('API Request Failed (Expected without backend). Simulating success for demo.')
                commit('SET_TOKEN', 'admin-token')
                commit('SET_USER_INFO', {
                  nickname: 'Super Admin',
                  mobile,
                  avatar: ''
                })
                commit('SET_ROLE_INFO', {
                  id: 1,
                  name: '超级管理员',
                  status: 1
                })
                commit('SET_IS_ADMIN', true)
                commit('SET_NAME', 'Super Admin')
                commit('SET_AVATAR', '')
                commit('SET_PERMISSION_CODES', ['*'])
                commit('SET_MENU_KEYS', ['dashboard', 'user', 'history', 'knowledge', 'official-file', 'sensitive', 'basic', 'rbac'])
                commit('SET_DEFAULT_HOME', '/dashboard')
                commit('SET_PERMISSION_VERSION', 'mock')
                commit('SET_PROFILE_LOADED', true)
                resolve()
              } else {
                reject(error)
              }
            })
          })
        },

        // get user info
        getInfo({ commit, state }) {
          return new Promise((resolve, reject) => {
            getInfo().then(response => {
              const { data } = response
              if (!data) {
                reject('Verification failed, please Login again.')
                return
              }

              const blockReason = getStatusBlockReason(data)
              if (blockReason) {
                commit('RESET_STATE')
                reject(new Error(blockReason))
                return
              }

              const isSuperAdmin = resolveIsAdminFlag(data, state.token)
              if (!isSuperAdmin) {
                commit('RESET_STATE')
                reject(new Error('当前账号无登录权限，请联系管理员开通'))
                return
              }

              const user = data.user || {}
              const role = data.role || null
              const name = data.name || user.nickname || user.mobile || ''
              const avatar = data.avatar || user.avatar || ''
              const lastLoginAt = data.last_login_at || user.last_login_at || ''
              const permissionCodes = data.permission_codes || data.permissionCodes || []
              const menuKeys = data.menu_keys || data.menuKeys || []
              const defaultHome = data.default_home || data.defaultHome || ''
              const resolvedHomePath = resolveDefaultHomePath({
                isSuperAdmin,
                permissionCodes,
                menuKeys
              }, defaultHome)

              if (resolvedHomePath === '/403') {
                commit('RESET_STATE')
                reject(new Error('当前账号暂无可访问菜单，请联系管理员配置权限'))
                return
              }

              commit('SET_USER_INFO', user)
              commit('SET_ROLE_INFO', role)
              commit('SET_IS_ADMIN', isSuperAdmin)
              commit('SET_NAME', name)
              commit('SET_AVATAR', avatar)
              commit('SET_LAST_LOGIN_AT', lastLoginAt)
              commit('SET_PERMISSION_CODES', permissionCodes)
              commit('SET_MENU_KEYS', menuKeys)
              commit('SET_DEFAULT_HOME', defaultHome)
              commit('SET_PERMISSION_VERSION', data.permission_version || data.permissionVersion || '')
              commit('SET_PROFILE_LOADED', true)
              resolve(data)
            }).catch(error => {
              if (isMockableNetworkError(error) && state.token === 'admin-token') {
                console.warn('API Request Failed (Expected without backend). Simulating success for demo.')
                const mockData = {
                  user: {
                    nickname: 'Super Admin',
                    mobile: '13000000000',
                    avatar: 'https://wpimg.wallstcn.com/f778738c-e4f8-4870-b634-56703b4acafe.gif'
                  },
                  role: {
                    id: 1,
                    name: '超级管理员',
                    status: 1
                  },
                  is_super_admin: 1,
                  permission_codes: ['*'],
                  menu_keys: ['dashboard', 'user', 'history', 'knowledge', 'official-file', 'sensitive', 'basic', 'rbac'],
                  default_home: '/dashboard',
                  permission_version: 'mock'
                }
                commit('SET_USER_INFO', mockData.user)
                commit('SET_ROLE_INFO', mockData.role)
                commit('SET_IS_ADMIN', true)
                commit('SET_NAME', mockData.user.nickname)
                commit('SET_AVATAR', mockData.user.avatar)
                commit('SET_LAST_LOGIN_AT', '')
                commit('SET_PERMISSION_CODES', mockData.permission_codes)
                commit('SET_MENU_KEYS', mockData.menu_keys)
                commit('SET_DEFAULT_HOME', mockData.default_home)
                commit('SET_PERMISSION_VERSION', mockData.permission_version)
                commit('SET_PROFILE_LOADED', true)
                resolve(mockData)
                return
              }
              reject(error)
            })
          })
        },

        // user logout
        logout({ commit, state }) {
          return new Promise((resolve, reject) => {
            logout(state.token).then(() => {
              commit('RESET_STATE')
              resolve()
            }).catch(error => {
               // 演示模式
              commit('RESET_STATE')
              resolve()
            })
          })
        },

        // remove token
        resetToken({ commit }) {
          return new Promise(resolve => {
            commit('RESET_STATE')
            resolve()
          })
        }
      },
      getters: {
        token: state => state.token,
        userInfo: state => state.user_info,
        roleInfo: state => state.role_info,
        name: state => state.name,
        avatar: state => state.avatar,
        last_login_at: state => state.last_login_at,
        isAdmin: state => state.is_admin,
        isSuperAdmin: state => state.is_admin,
        profileLoaded: state => state.profile_loaded,
        permissionCodes: state => state.permission_codes,
        menuKeys: state => state.menu_keys,
        defaultHome: state => state.default_home,
        permissionVersion: state => state.permission_version
      }
    }
  },
  getters: {
    token: state => state.user.token,
    userInfo: state => state.user.user_info,
    roleInfo: state => state.user.role_info,
    name: state => state.user.name,
    avatar: state => state.user.avatar,
    last_login_at: state => state.user.last_login_at,
    isAdmin: state => state.user.is_admin,
    isSuperAdmin: state => state.user.is_admin,
    profileLoaded: state => state.user.profile_loaded,
    permissionCodes: state => state.user.permission_codes,
    menuKeys: state => state.user.menu_keys,
    defaultHome: state => state.user.default_home,
    permissionVersion: state => state.user.permission_version
  }
})
